import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  // Auto-fill the referral code from the ?ref= landing capture (persisted in
  // localStorage by Landing.jsx). Also allow overriding via the current URL.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("ref");
      const fromStorage = localStorage.getItem("bliencer_ref");
      const code = fromUrl || fromStorage || "";
      if (code) setRef(code);
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name,
        email,
        password,
        referral_code: ref || undefined,
      });
      loginWithToken(data.token, data.user);
      // Attribution consumed — clear so a later user on the same device
      // doesn't accidentally get credited to the same affiliate.
      try {
        localStorage.removeItem("bliencer_ref");
      } catch {}
      toast.success(`Account created. Welcome, ${data.user.name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-32 pb-24 px-6 flex justify-center">
        <div className="w-full max-w-md">
          <h1 className="text-4xl font-bold tracking-tighter" data-testid="signup-title">
            Create your account
          </h1>
          <p className="text-[#a0a0a0] mt-3">
            Start ordering premium human-edited clips in minutes.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="signup-form">
            <div>
              <label className="text-xs text-[#a0a0a0] uppercase tracking-widest">Name</label>
              <input
                data-testid="signup-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 focus:border-white outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-[#a0a0a0] uppercase tracking-widest">Email</label>
              <input
                data-testid="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 focus:border-white outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-[#a0a0a0] uppercase tracking-widest">Password</label>
              <input
                data-testid="signup-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 focus:border-white outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-[#a0a0a0] uppercase tracking-widest">
                Referral code (optional)
              </label>
              <input
                data-testid="signup-ref"
                value={ref}
                onChange={(e) => setRef(e.target.value.toUpperCase())}
                className="mt-2 w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 focus:border-white outline-none tracking-wider"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full squircle bg-white text-black py-3.5 font-medium hover:bg-[#f2f2f2] btn-anim inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Creating…" : "Create account"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="my-8 flex items-center gap-3 text-xs text-[#666]">
            <span className="h-px flex-1 bg-[#1e1e1e]" /> OR <span className="h-px flex-1 bg-[#1e1e1e]" />
          </div>

          <button
            data-testid="signup-google"
            onClick={googleLogin}
            className="w-full squircle border border-[#2b2b2b] py-3.5 font-medium hover:bg-[#111] btn-anim"
          >
            Continue with Google
          </button>

          <div className="mt-8 text-sm text-[#a0a0a0]">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-login-link" className="text-white underline">
              Log in
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
